import { Flex, Text, TimerIcon, useTooltip } from 'vvs-uikit'
import { useTheme } from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import { MoleDashInfo } from 'state/vvsgotchi/quests/moleDash'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { QuestWithConfig } from 'state/vvsgotchi/models'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import useEndOfUTCDayCountdown from 'hooks/useEndOfUTCDayCountdown'
import BestRecordLabel from './BestRecordLabel'
import ExpStars from './ExpStars'
import { RetroText, RetroBaseText, GameInfoFlex } from './styles'

import { MOLE_DASH_MAX_EXP } from '../config'
import { formatLasted } from '../utils'
import { MoleDashGameResult } from '../types'

export const InitGameInfoFlex: React.FC<{ moleDashInfo: MoleDashInfo; quest: QuestWithConfig }> = ({
  moleDashInfo,
  quest,
}) => {
  const { t } = useTranslation()
  const isCompleted = quest.isCompleted || moleDashInfo.todayExp >= MOLE_DASH_MAX_EXP
  const {
    targetRef: tooltipTargetRef,
    tooltip,
    tooltipVisible,
  } = useTooltip(
    <Text fontSize="12px" fontWeight="400" maxWidth="250px">
      {isCompleted
        ? t(
            'You have reached the maximum EXP rewards today, you can earn again tomorrow. As Mole Dash lover, you can continue to play and practice your dashing skill!',
          )
        : t('Don’t missed the chance of earning maximum 5 EXP per day!')}
    </Text>,
    { placement: 'top-start', tooltipOffset: [-5, 15] },
  )

  return (
    <>
      <Flex justifyContent="end" m={1}>
        <BestRecordLabel lasted={moleDashInfo.bestRecord.lasted} />
      </Flex>
      <GameInfoFlex
        justifyContent="space-between"
        alignItems="center"
        bg={isCompleted ? '/images/vvsGotchi/bg_mint.png' : null}
      >
        <Flex ref={tooltipTargetRef} flexDirection="column">
          <RetroText>
            {t('%currentExp%/%maxExp%', {
              currentExp: moleDashInfo.todayExp,
              maxExp: MOLE_DASH_MAX_EXP,
            })}
          </RetroText>
          <RetroText fontSize="8px" maxWidth="32px">
            {t('Gained Today')}
          </RetroText>
        </Flex>

        {tooltipVisible && tooltip}

        <ExpStars exp={moleDashInfo.todayExp} />
      </GameInfoFlex>

      {isCompleted && <ResetSection />}
    </>
  )
}

const ResetSection = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { refresh } = useGotchiOwner()
  const timeExcerptMap = useEndOfUTCDayCountdown(refresh)

  return (
    <Flex background="black" alignItems="center" p="6px 0px 4px 12px" gap="8px">
      <TimerIcon width="14px" fill={theme.colors.lightBeige} />
      <Text color="lightBeige" fontSize="13px" fontWeight="700">
        {t('Reward resets in %time%', {
          time: timeExcerptMap.h > 0 ? t('%h%hr %m%m', timeExcerptMap) : t('%m%m %s%s', timeExcerptMap),
        })}
      </Text>
    </Flex>
  )
}

export const ResultGameInfoFlex: React.FC<{ moleDashInfo: MoleDashInfo; gameResult: MoleDashGameResult }> = ({
  moleDashInfo,
  gameResult,
}) => {
  const { t } = useTranslation()

  if (!gameResult || !moleDashInfo) {
    return (
      <GameInfoFlex>
        <Flex flex="1" flexDirection="column" alignItems="center" pb={2}>
          <RetroText>{t('Loading...')}</RetroText>
        </Flex>
      </GameInfoFlex>
    )
  }

  const expGained = gameResult.gainedExp > 0
  const maxExpGained = moleDashInfo.todayExp >= MOLE_DASH_MAX_EXP
  return (
    <GameInfoFlex>
      <Flex flex="1" flexDirection="column" alignItems="start" pb={2}>
        <RetroText fontSize="8px">{t('Lasted')}</RetroText>
        <RetroText mb={1}>{formatLasted(gameResult.lasted, t)}</RetroText>
        <BestRecordLabel lasted={moleDashInfo.bestRecord.lasted} />
      </Flex>

      <Flex flex="1" flexDirection="column" alignItems="start" pb={2}>
        {(expGained || maxExpGained) && <RetroText fontSize="8px">{t('Earned')}</RetroText>}
        {expGained && (
          <Flex alignItems="center">
            <PixelatedImage src="/images/vvsGotchi/star.png" width="15px" />
            <RetroBaseText color="success" ml={1}>
              {t('+%exp%', { exp: gameResult.gainedExp })}
            </RetroBaseText>
          </Flex>
        )}
        {maxExpGained && (
          <RetroBaseText color="darkGrey" fontSize="10px" lineHeight="10px">
            {t('You have met today max rewards!')}
          </RetroBaseText>
        )}
      </Flex>
    </GameInfoFlex>
  )
}
